import { useEffect, useMemo, useState } from "react";
import client from "../api/client";

const today = () => new Date().toISOString().split("T")[0];
const monthStart = () => today().slice(0, 8) + "01";
const companyId = () => Number(localStorage.getItem("companyId") || 0);

const val = (r, ...keys) => {
  for (const k of keys) {
    const x = r?.[k];
    if (x !== undefined && x !== null) return x;
  }
  return "";
};

const safe = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return "";
};

const arr = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.result)) return data.result;
  return [];
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const money = (v) =>
  num(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const errText = (e, fallback) => {
  const d = e?.response?.data;
  if (typeof d === "string" && d.trim()) return d;
  if (typeof d?.message === "string" && d.message.trim()) return d.message;
  if (typeof d?.title === "string" && d.title.trim()) return d.title;
  if (typeof e?.message === "string" && e.message.trim()) return e.message;
  return fallback;
};

const csvCell = (v) => {
  const s = safe(v).replace(/"/g, '""');
  return `"${s}"`;
};

const styles = {
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 700 },
  td: { fontSize: 11, padding: "6px 8px", whiteSpace: "nowrap" },
};

export default function SalesRegisterPage() {
  const [customers, setCustomers] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filter, setFilter] = useState({
    fromDate: monthStart(),
    toDate: today(),
    customerId: "",
    search: "",
  });

  const filteredRows = useMemo(() => {
    const s = safe(filter.search).toLowerCase().trim();
    if (!s) return rows;

    return rows.filter((r) =>
      Object.values(r || {})
        .map(safe)
        .join(" ")
        .toLowerCase()
        .includes(s),
    );
  }, [rows, filter.search]);

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (a, r) => ({
        subTotal: a.subTotal + num(val(r, "SubTotal", "subTotal")),
        cgst: a.cgst + num(val(r, "CGSTAmount", "cgstAmount")),
        sgst: a.sgst + num(val(r, "SGSTAmount", "sgstAmount")),
        igst: a.igst + num(val(r, "IGSTAmount", "igstAmount")),
        tax: a.tax + num(val(r, "TaxAmount", "taxAmount")),
        discount: a.discount + num(val(r, "DiscountAmount", "discountAmount")),
        grand: a.grand + num(val(r, "GrandTotal", "grandTotal")),
      }),
      {
        subTotal: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        tax: 0,
        discount: 0,
        grand: 0,
      },
    );
  }, [filteredRows]);

  useEffect(() => {
    loadCustomers();
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCustomers = async () => {
    try {
      const res = await client.get("/sales/customers", {
        params: { companyId: companyId() || undefined },
      });

      setCustomers(arr(res.data));
    } catch (e) {
      console.error(e);
      setCustomers([]);
    }
  };

  const load = async () => {
    if (filter.fromDate && filter.toDate && filter.fromDate > filter.toDate) {
      return alert("From date cannot be after To date");
    }

    try {
      setLoading(true);

      const res = await client.get("/sales/sales-register", {
        params: {
          companyId: companyId() || undefined,
          fromDate: filter.fromDate || undefined,
          toDate: filter.toDate || undefined,
          customerId: filter.customerId || undefined,
        },
      });

      setRows(arr(res.data));
    } catch (e) {
      console.error(e);
      alert(errText(e, "Failed to load sales register"));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    const headers = [
      "Invoice No",
      "Date",
      "Customer",
      "GSTIN",
      "Sub Total",
      "CGST",
      "SGST",
      "IGST",
      "Tax",
      "Discount",
      "Grand Total",
      "Notes",
    ];

    const lines = filteredRows.map((r) =>
      [
        val(r, "InvoiceNo", "invoiceNo"),
        safe(val(r, "InvoiceDate", "invoiceDate")).slice(0, 10),
        val(r, "CustomerName", "customerName"),
        val(r, "GSTIN", "gstin"),
        num(val(r, "SubTotal", "subTotal")),
        num(val(r, "CGSTAmount", "cgstAmount")),
        num(val(r, "SGSTAmount", "sgstAmount")),
        num(val(r, "IGSTAmount", "igstAmount")),
        num(val(r, "TaxAmount", "taxAmount")),
        num(val(r, "DiscountAmount", "discountAmount")),
        num(val(r, "GrandTotal", "grandTotal")),
        val(r, "Notes", "notes"),
      ]
        .map(csvCell)
        .join(","),
    );

    const totalLine = [
      "TOTAL",
      "",
      "",
      "",
      totals.subTotal,
      totals.cgst,
      totals.sgst,
      totals.igst,
      totals.tax,
      totals.discount,
      totals.grand,
      "",
    ]
      .map(csvCell)
      .join(",");

    const csv = [headers.map(csvCell).join(","), ...lines, totalLine].join(
      "\n",
    );
    const blob = new Blob([`\uFEFF${csv}`], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sales-register-${filter.fromDate || "all"}-to-${
      filter.toDate || "all"
    }.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="container-fluid py-2" style={{ fontSize: 11, padding: 0 }}>
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 2px 10px rgba(0,0,0,.08)",
        }}
      >
        <div
          style={{
            background:
              "linear-gradient(90deg, rgb(11, 79, 74), rgb(15, 118, 110))",
            color: "#fff",
            padding: "9px 12px",
          }}
        >
          <h6 style={{ fontSize: 13, fontWeight: 800, margin: 0 }}>
            <i className="bi bi-journal-text me-2"></i>
            Sales Register
          </h6>
          <div style={{ fontSize: 10, opacity: 0.85 }}>
            Date-wise, customer-wise and GST-wise sales report
          </div>
        </div>

        <div style={{ padding: 10 }}>
          <div className="row g-2 mb-2">
            <Stat title="Taxable" value={`₹ ${money(totals.subTotal)}`} />
            <Stat title="GST" value={`₹ ${money(totals.tax)}`} />
            <Stat title="Discount" value={`₹ ${money(totals.discount)}`} />
            <Stat title="Sales Total" value={`₹ ${money(totals.grand)}`} />
          </div>

          <div className="row g-2 mb-2">
            <Field label="From">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={filter.fromDate}
                onChange={(e) =>
                  setFilter({ ...filter, fromDate: e.target.value })
                }
              />
            </Field>

            <Field label="To">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={filter.toDate}
                onChange={(e) =>
                  setFilter({ ...filter, toDate: e.target.value })
                }
              />
            </Field>

            <Field label="Customer" col="col-md-3">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={filter.customerId}
                onChange={(e) =>
                  setFilter({ ...filter, customerId: e.target.value })
                }
              >
                <option value="">All Customers</option>
                {customers.map((c, index) => {
                  const id = val(c, "Id", "id") || index;
                  return (
                    <option key={id} value={id}>
                      {safe(
                        val(c, "CustomerName", "customerName", "Name", "name"),
                      )}
                    </option>
                  );
                })}
              </select>
            </Field>

            <Field label="Search" col="col-md-3">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={filter.search}
                placeholder="Invoice/customer/GSTIN"
                onChange={(e) =>
                  setFilter({ ...filter, search: e.target.value })
                }
              />
            </Field>

            <div className="col-md-1 d-flex align-items-end">
              <button
                type="button"
                className="btn btn-primary btn-sm w-100"
                style={styles.btn}
                onClick={load}
                disabled={loading}
              >
                {loading ? "..." : "Load"}
              </button>
            </div>

            <div className="col-md-1 d-flex align-items-end">
              <button
                type="button"
                className="btn btn-success btn-sm w-100"
                style={styles.btn}
                onClick={exportCsv}
                disabled={loading || filteredRows.length === 0}
              >
                CSV
              </button>
            </div>
          </div>

          <div
            style={{
              maxHeight: 520,
              overflow: "auto",
              border: "1px solid #e9ecef",
              borderRadius: 10,
            }}
          >
            <table
              className="table table-bordered table-hover align-middle"
              style={{ fontSize: 11, minWidth: 1450, marginBottom: 0 }}
            >
              <thead>
                <tr>
                  {[
                    "Invoice No",
                    "Date",
                    "Customer",
                    "GSTIN",
                    "Taxable",
                    "CGST",
                    "SGST",
                    "IGST",
                    "Tax",
                    "Discount",
                    "Grand Total",
                    "Notes",
                  ].map((h) => (
                    <TH key={h}>{h}</TH>
                  ))}
                </tr>
              </thead>

              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="12"
                      className="text-center text-muted"
                      style={{ padding: 18 }}
                    >
                      {loading ? "Loading..." : "No sales records found"}
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((r, index) => (
                    <tr key={val(r, "Id", "id") || index}>
                      <td style={{ ...styles.td, fontWeight: 800 }}>
                        {safe(val(r, "InvoiceNo", "invoiceNo"))}
                      </td>
                      <td style={styles.td}>
                        {safe(val(r, "InvoiceDate", "invoiceDate")).slice(
                          0,
                          10,
                        )}
                      </td>
                      <td style={styles.td}>
                        {safe(val(r, "CustomerName", "customerName"))}
                      </td>
                      <td style={styles.td}>
                        {safe(val(r, "GSTIN", "gstin")) || "-"}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {money(val(r, "SubTotal", "subTotal"))}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {money(val(r, "CGSTAmount", "cgstAmount"))}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {money(val(r, "SGSTAmount", "sgstAmount"))}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {money(val(r, "IGSTAmount", "igstAmount"))}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {money(val(r, "TaxAmount", "taxAmount"))}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {money(val(r, "DiscountAmount", "discountAmount"))}
                      </td>
                      <td className="text-end fw-bold" style={styles.td}>
                        {money(val(r, "GrandTotal", "grandTotal"))}
                      </td>
                      <td style={styles.td}>
                        {safe(val(r, "Notes", "notes")) || "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>

              <tfoot>
                <tr style={{ background: "#f8fafc", fontWeight: 800 }}>
                  <td colSpan="4" className="text-end" style={styles.td}>
                    Total
                  </td>
                  <td className="text-end" style={styles.td}>
                    {money(totals.subTotal)}
                  </td>
                  <td className="text-end" style={styles.td}>
                    {money(totals.cgst)}
                  </td>
                  <td className="text-end" style={styles.td}>
                    {money(totals.sgst)}
                  </td>
                  <td className="text-end" style={styles.td}>
                    {money(totals.igst)}
                  </td>
                  <td className="text-end" style={styles.td}>
                    {money(totals.tax)}
                  </td>
                  <td className="text-end" style={styles.td}>
                    {money(totals.discount)}
                  </td>
                  <td className="text-end" style={styles.td}>
                    {money(totals.grand)}
                  </td>
                  <td style={styles.td}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, col = "col-md-2" }) {
  return (
    <div className={col}>
      <label
        style={{
          fontSize: 10,
          fontWeight: 700,
          marginBottom: 3,
          color: "#495057",
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function Stat({ title, value }) {
  return (
    <div className="col-md-3">
      <div
        style={{
          border: "1px solid #e9ecef",
          background: "#f8fafc",
          borderRadius: 10,
          padding: 9,
          borderTop: "3px solid #0f766e",
        }}
      >
        <div style={{ fontSize: 10, color: "#64748b", fontWeight: 800 }}>
          {title}
        </div>
        <div style={{ fontSize: 15, color: "#0f766e", fontWeight: 900 }}>
          {safe(value)}
        </div>
      </div>
    </div>
  );
}

function TH({ children }) {
  return (
    <th
      style={{
        fontSize: 10,
        color: "#fff",
        background: "#07111f",
        padding: "7px 8px",
        whiteSpace: "nowrap",
        position: "sticky",
        top: 0,
      }}
    >
      {children}
    </th>
  );
}
